<template>
  <div id="app">
    <guest-navbar v-if="showGuestChrome" />
    <div class="app-shell">
      <app-sidebar v-if="showSidebar" />
      <main class="app-main">
        <div
          v-if="showFreePlanNotice"
          role="status"
          class="flex items-center justify-between gap-3 border-b border-border bg-primary/5 px-4 py-2.5 text-sm text-foreground"
        >
          <p class="leading-relaxed">
            Your trial or plan has ended and you're now on the Free plan.
            <router-link to="/subscription/plans" class="font-semibold text-primary underline-offset-2 hover:underline">Upgrade</router-link>
          </p>
          <button
            type="button"
            class="shrink-0 rounded-md px-2 py-1 text-muted-foreground hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            aria-label="Dismiss"
            @click="dismissFreePlanNotice"
          >
            <i class="fas fa-times" aria-hidden="true"></i>
          </button>
        </div>
        <router-view />
      </main>
    </div>
    <footer-component v-if="!isHomePage && showGuestChrome && !route.meta?.ownFooter" />
    <landing-version-switch v-if="isGuestPreview" />
    <!-- Fallback chatbot: show old in-app bot when Crisp is not configured -->
    <floating-chatbot v-if="false && !crispEnabled && !isHomePage" />
    <upgrade-modal />
    <extension-prompt-modal v-if="isLoggedIn && !isGuestPreview" />
  </div>
</template>

<script>
import FooterComponent from "@/components/layout/FooterComponent.vue";
import AppSidebar from "@/components/layout/AppSidebar.vue";
import GuestNavbar from "@/components/layout/GuestNavbar.vue";
import LandingVersionSwitch from "@/components/admin/LandingVersionSwitch.vue";
import { useGuestPreview } from "@/composables/useLandingVersion";
import FloatingChatbot from "@/components/FloatingChatbot.vue";
import UpgradeModal from "@/components/UpgradeModal.vue";
import ExtensionPromptModal from "@/components/ExtensionPromptModal.vue";
import { computed, ref, onMounted, watch } from "vue";
import authStore from "@/store/auth";
import { useRoute } from "vue-router";
import {
  initCrisp,
  resetCrispSession,
  setCrispUserContext,
  setCrispVisibility,
} from "@/services/crisp";

export default {
  name: "App",
  components: {
    FooterComponent,
    AppSidebar,
    GuestNavbar,
    LandingVersionSwitch,
    FloatingChatbot,
    UpgradeModal,
    ExtensionPromptModal,
  },
  setup() {
    const isLoggedIn = computed(() => authStore.state.isLoggedIn);
    const user = computed(() => authStore.state.user);
    const route = useRoute();
    const isHomePage = computed(
      () =>
        route.path === "/hire-developers" ||
        route.path.startsWith("/admin-dashboard"),
    );
    // Guest chrome (sidebar/navbar + footer) is for logged-out visitors, and for admins
    // in guest preview (Admin → Landing Page) so they see exactly what visitors see.
    const { isGuestPreview } = useGuestPreview();
    // Visitors get GuestNavbar on every public page. Pages with meta.ownFooter (the
    // homepage) render their own footer instead of FooterComponent.
    const showGuestChrome = computed(
      () => !isLoggedIn.value || isGuestPreview.value,
    );
    const showSidebar = computed(
      () => isLoggedIn.value && !!route.meta?.appShell && !isGuestPreview.value,
    );
    const crispWebsiteId = (process.env.VUE_APP_CRISP_WEBSITE_ID || "").trim();
    const crispEnabled = crispWebsiteId.length > 0;

    // Small, dismissible notice for users whose 14-day trial has ended and who
    // now sit on the Free plan. Never blocks the app.
    const readDismissed = () => {
      try { return localStorage.getItem("free_plan_notice_dismissed"); } catch (e) { return null; }
    };
    const noticeDismissedFor = ref(readDismissed());
    // Dismissing hides it until the user's plan changes again.
    const freeNoticeKey = (u) => `${u.subscription?.plan?.plan_type}:${u.subscription?.end_date || u.trial_ends_at || ""}`;
    const showFreePlanNotice = computed(() => {
      const u = user.value;
      if (!isLoggedIn.value || !showSidebar.value || !u || u.account_type !== "jobseeker") return false;
      if (u.is_trial_active) return false;
      // On Free: either already moved there, or the trial/plan lapsed and the
      // expiry job hasn't moved the subscription yet (Free limits apply already).
      const onFree = u.subscription?.plan?.plan_type === "free" || u.has_active_subscription === false;
      if (!onFree || !(u.trial_ends_at || u.subscription?.end_date)) return false;
      return noticeDismissedFor.value !== freeNoticeKey(u);
    });
    const dismissFreePlanNotice = () => {
      const key = user.value ? freeNoticeKey(user.value) : "1";
      try { localStorage.setItem("free_plan_notice_dismissed", key); } catch (e) { /* storage blocked */ }
      noticeDismissedFor.value = key;
    };

    const syncCrispUser = () => {
      if (!crispEnabled) {
        return;
      }

      if (!isLoggedIn.value || !user.value) {
        return;
      }

      const displayName =
        `${user.value.first_name || ""} ${user.value.last_name || ""}`.trim() ||
        user.value.username ||
        user.value.email ||
        "Applypa User";

      setCrispUserContext({
        userId: user.value.id,
        email: user.value.email,
        nickname: displayName,
        accountType: user.value.account_type,
      });
    };

    const syncCrispVisibility = () => {
      if (!crispEnabled) {
        return;
      }

      // Keep the same visibility behavior as the old in-app floating chatbot.
      setCrispVisibility(!isHomePage.value);
    };

    // Check subscription status when app mounts
    onMounted(async () => {
      try {
        await authStore.checkAuthStatus();
        if (authStore.state.isLoggedIn) {
          await authStore.checkSubscriptionStatus();
        }
        if (crispEnabled && initCrisp(crispWebsiteId)) {
          syncCrispUser();
          syncCrispVisibility();
        }
      } catch (e) {
        // Only check authentication, not subscription
      }
    });

    // Check subscription on route change
    watch(
      () => route.path,
      async () => {
        syncCrispVisibility();
        // Only check authentication, not subscription
      },
    );

    // Watch for login status changes
    watch(isLoggedIn, async (loggedIn, wasLoggedIn) => {
      if (loggedIn) {
        try {
          await authStore.checkSubscriptionStatus();
        } catch (e) {
          // Ignore - modal simply won't show if this fails
        }
      }
      if (crispEnabled && loggedIn) {
        syncCrispUser();
      }
      if (crispEnabled && !loggedIn && wasLoggedIn) {
        // Avoid leaking previous user's support history on shared browsers.
        resetCrispSession();
      }
      // Only check authentication, not subscription
    });

    watch(
      user,
      () => {
        if (crispEnabled && isLoggedIn.value) {
          syncCrispUser();
        }
      },
      { deep: true },
    );

    return {
      isLoggedIn,
      user,
      crispEnabled,
      showFreePlanNotice,
      dismissFreePlanNotice,
      isHomePage,
      route,
      showSidebar,
      showGuestChrome,
      isGuestPreview,
    };
  },
};
</script>

<style>
/* Import any global styles here */
body {
  margin: 0;
  padding: 0;
  font-family: 'Urbanist', sans-serif;
}

.app-shell {
  display: flex;
}

.app-main {
  flex: 1;
  min-width: 0;
  min-height: calc(
    100vh - 200px
  ); /* Adjust based on your header/footer height */
}

.app-shell:has(> .guest-sidebar) .app-main {
  margin-left: 220px;
}

#app:has(.guest-sidebar) > footer {
  margin-left: 220px;
}

@media (max-width: 767px) {
  .app-shell:has(.app-sidebar) .app-main {
    padding-top: 52px;
  }

  .app-shell:has(> .guest-sidebar) .app-main {
    margin-left: 0;
  }

  #app:has(.guest-sidebar) > footer {
    margin-left: 0;
  }
}
</style>
